<template>
  <svg
    class="inspection-hud pointer-events-none absolute inset-0 h-full w-full"
    :class="{ 'is-frozen': frozen, 'is-still': still }"
    viewBox="0 0 1600 900"
    preserveAspectRatio="xMidYMid meet"
    aria-hidden="true"
  >
    <defs>
      <filter :id="`${uid}-glow`" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="4" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <clipPath :id="`${uid}-zone`">
        <rect x="70" y="50" width="1460" height="800" rx="36" />
      </clipPath>
      <mask :id="`${uid}-vignette`">
        <rect width="1600" height="900" fill="white" />
        <rect x="70" y="50" width="1460" height="800" rx="36" fill="black" />
      </mask>
    </defs>

    <template v-if="!still">
      <rect width="1600" height="900" fill="black" opacity="0.25" :mask="`url(#${uid}-vignette)`" />
      <path
        v-for="corner in corners"
        :key="corner"
        :d="corner"
        fill="none"
        stroke="currentColor"
        stroke-width="4"
        stroke-linecap="round"
        opacity="0.8"
      />
    </template>

    <g :clip-path="`url(#${uid}-zone)`">
      <g :key="shotKey" class="hud-shape" :transform="shapeTransform">
        <path
          v-if="guide.overlay.ground"
          :d="guide.overlay.ground"
          fill="none"
          stroke="currentColor"
          :stroke-width="3 * strokeScale"
          :stroke-dasharray="`${4 * strokeScale} ${18 * strokeScale}`"
          stroke-linecap="round"
          opacity="0.35"
        />
        <g
          class="hud-details"
          fill="none"
          stroke="currentColor"
          :stroke-width="2.5 * strokeScale"
          stroke-linejoin="round"
          stroke-linecap="round"
        >
          <path v-for="(d, i) in guide.overlay.details" :key="`d${i}`" :d="d" pathLength="1" />
          <circle
            v-for="([cx, cy, r], i) in guide.overlay.circles ?? []"
            :key="`c${i}`"
            :cx="cx"
            :cy="cy"
            :r="r"
            pathLength="1"
          />
        </g>
        <g
          class="hud-outline"
          fill="none"
          stroke="currentColor"
          :stroke-width="5 * strokeScale"
          stroke-linejoin="round"
          stroke-linecap="round"
          :filter="still ? undefined : `url(#${uid}-glow)`"
        >
          <path v-for="(d, i) in guide.overlay.outline" :key="`o${i}`" :d="d" pathLength="1" />
        </g>
      </g>
    </g>
  </svg>
</template>

<script setup lang="ts">
import type { InspectionShotKey } from '@carvia/ros-client-types';
import { computed, useId } from 'vue';
import { getShotGuide } from './inspection-shots';

const props = defineProps<{
  shotKey: InspectionShotKey;
  frozen?: boolean;
  /** Static silhouette without HUD chrome or animation, in the surrounding text colour. */
  still?: boolean;
}>();

const uid = `hud-${useId()}`;
const guide = computed(() => getShotGuide(props.shotKey));
const shapeTransform = computed(() => {
  const { mirror, zoom } = guide.value.overlay;
  const parts = [
    mirror && 'translate(1600 0) scale(-1 1)',
    zoom &&
      `translate(${zoom.to[0]} ${zoom.to[1]}) scale(${zoom.scale}) translate(${-zoom.from[0]} ${-zoom.from[1]})`,
  ];
  return parts.filter(Boolean).join(' ') || undefined;
});
/** Keeps line weights constant on zoomed drawings. */
const strokeScale = computed(() => 1 / (guide.value.overlay.zoom?.scale ?? 1));

const corners = [
  'M 70 140 V 86 Q 70 50 106 50 H 160',
  'M 1440 50 H 1494 Q 1530 50 1530 86 V 140',
  'M 1530 760 V 814 Q 1530 850 1494 850 H 1440',
  'M 160 850 H 106 Q 70 850 70 814 V 760',
];
</script>

<style scoped>
.inspection-hud {
  color: var(--color-hazel-300);
  transition: color 300ms ease;
}
.inspection-hud.is-still {
  color: inherit;
}

.hud-outline path,
.hud-details path,
.hud-details circle {
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: hud-draw 700ms cubic-bezier(0.65, 0, 0.35, 1) forwards;
}
.hud-details {
  opacity: 0.5;
}
.hud-details path,
.hud-details circle {
  animation-delay: 150ms;
}
.is-frozen .hud-shape {
  opacity: 0.35;
  transition: opacity 300ms ease;
}
/* Over a photo the shape is shown at once: it mounts again when the photo replaces the camera. */
.is-still .hud-outline path,
.is-still .hud-details path,
.is-still .hud-details circle,
.is-frozen .hud-outline path,
.is-frozen .hud-details path,
.is-frozen .hud-details circle {
  animation: none;
  stroke-dashoffset: 0;
}

@keyframes hud-draw {
  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hud-outline path,
  .hud-details path,
  .hud-details circle {
    animation: none;
    stroke-dashoffset: 0;
  }
}
</style>
